import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod/v3';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { LocalContact, LocalContactItem } from '@/context/create-account-flow';
import { maskCpf, maskRg } from '@/lib/formatters';

const contactItemSchema = z.object({
  id: z.string(),
  contact: z.string().min(1, 'Obrigatório'),
  type: z.enum(['Celular', 'Telefone Residencial', 'Telefone Comercial', 'Email']),
  isWhatsapp: z.boolean().optional(),
  isPreferred: z.boolean().optional(),
});

const contactSchema = z.object({
  name: z.string().min(1, 'Nome obrigatório'),
  positionOrRelation: z.string().optional(),
  area: z.string().min(1, 'Área obrigatória'),
  cpf: z.string().optional(),
  rg: z.string().optional(),
  contractEnabled: z.boolean().optional(),
  contactFor: z.array(z.string()).min(1, 'Selecione ao menos um'),
  contactItems: z.array(contactItemSchema).min(1, 'Adicione ao menos um contato'),
});

type ContactFormData = z.infer<typeof contactSchema>;

const DEFAULT_CONTACT_FOR_OPTIONS = ['Comercial', 'Faturamento', 'Marketing', 'Suporte'];
const CONTACT_TYPES: LocalContactItem['type'][] = [
  'Celular',
  'Telefone Residencial',
  'Telefone Comercial',
  'Email',
];

interface ContactFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: LocalContact) => void | Promise<void>;
  contact?: LocalContact;
  mode?: 'create' | 'edit';
  duplicateMode?: boolean;
  contactForOptions?: string[];
}

export function ContactFormDialog({
  open,
  onClose,
  onSubmit,
  contact,
  mode = 'create',
  duplicateMode = false,
  contactForOptions = DEFAULT_CONTACT_FOR_OPTIONS,
}: ContactFormDialogProps) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      positionOrRelation: '',
      area: '',
      cpf: '',
      rg: '',
      contractEnabled: false,
      contactFor: [],
      contactItems: [
        {
          id: crypto.randomUUID(),
          contact: '',
          type: 'Celular',
          isWhatsapp: false,
          isPreferred: false,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'contactItems' });
  const selectedContactFor = watch('contactFor') || [];
  const contractEnabled = watch('contractEnabled');

  useEffect(() => {
    if (open) {
      if (duplicateMode && contact) {
        reset({
          name: contact.name || '',
          positionOrRelation: contact.positionOrRelation || '',
          area: contact.area || '',
          cpf: maskCpf(contact.cpf || ''),
          rg: maskRg(contact.rg || ''),
          contractEnabled: contact.contractEnabled || false,
          contactFor: [],
          contactItems: contact.contactItems.length
            ? contact.contactItems.map((item) => ({
                id: crypto.randomUUID(),
                contact: item.contact,
                type: item.type,
                isWhatsapp: item.preferredContact?.whatsapp || false,
                isPreferred: item.preferredContact?.phone || item.preferredContact?.email || false,
              }))
            : [
                {
                  id: crypto.randomUUID(),
                  contact: '',
                  type: 'Celular',
                  isWhatsapp: false,
                  isPreferred: false,
                },
              ],
        });
      } else if (mode === 'edit' && contact) {
        reset({
          name: contact.name || '',
          positionOrRelation: contact.positionOrRelation || '',
          area: contact.area || '',
          cpf: maskCpf(contact.cpf || ''),
          rg: maskRg(contact.rg || ''),
          contractEnabled: contact.contractEnabled || false,
          contactFor: contact.contactFor || [],
          contactItems: contact.contactItems.length
            ? contact.contactItems.map((item) => ({
                id: item.id,
                contact: item.contact,
                type: item.type,
                isWhatsapp: item.preferredContact?.whatsapp || false,
                isPreferred: item.preferredContact?.phone || item.preferredContact?.email || false,
              }))
            : [
                {
                  id: crypto.randomUUID(),
                  contact: '',
                  type: 'Celular',
                  isWhatsapp: false,
                  isPreferred: false,
                },
              ],
        });
      } else {
        reset({
          name: '',
          positionOrRelation: '',
          area: '',
          cpf: '',
          rg: '',
          contractEnabled: false,
          contactFor: [],
          contactItems: [
            {
              id: crypto.randomUUID(),
              contact: '',
              type: 'Celular',
              isWhatsapp: false,
              isPreferred: false,
            },
          ],
        });
      }
    }
  }, [open, contact, mode, duplicateMode, reset]);

  const handleContactForChange = (option: string, checked: boolean) => {
    if (checked) {
      setValue('contactFor', [...selectedContactFor, option]);
    } else {
      setValue(
        'contactFor',
        selectedContactFor.filter((c) => c !== option),
      );
    }
  };

  const onFormSubmit = async (data: ContactFormData) => {
    const contactData: LocalContact = {
      id: duplicateMode ? crypto.randomUUID() : contact?.id || crypto.randomUUID(),
      name: data.name,
      positionOrRelation: data.positionOrRelation || '',
      area: data.area || '',
      cpf: data.cpf || '',
      rg: data.rg || '',
      contractEnabled: data.contractEnabled || false,
      contactFor: data.contactFor,
      contactItems: data.contactItems.map((item) => ({
        id: item.id,
        contact: item.contact,
        type: item.type,
        preferredContact: {
          whatsapp: item.type === 'Celular' ? item.isWhatsapp : undefined,
          phone: ['Celular', 'Telefone Residencial', 'Telefone Comercial'].includes(item.type)
            ? item.isPreferred
            : undefined,
          email: item.type === 'Email' ? item.isPreferred : undefined,
        },
      })),
      originType: 'manual',
    };
    await onSubmit(contactData);
    onClose();
  };

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 11)
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {duplicateMode
              ? 'Duplicar Contato'
              : mode === 'edit'
                ? 'Editar Contato'
                : 'Novo Contato'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          {/* Contato para contrato */}
          <Controller
            name="contractEnabled"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="contractEnabled"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={duplicateMode}
                />
                <Label htmlFor="contractEnabled" className="text-sm cursor-pointer select-none">
                  Contato para assinatura de contrato
                </Label>
              </div>
            )}
          />

          {/* Nome */}
          <div className="space-y-1.5">
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="Nome do contato"
              disabled={duplicateMode}
            />
            {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
          </div>

          {/* Cargo */}
          <div className="space-y-1.5">
            <Label htmlFor="positionOrRelation">Cargo / Relação</Label>
            <Input
              id="positionOrRelation"
              {...register('positionOrRelation')}
              placeholder="Ex: Gerente, Sócio, Proprietário"
              disabled={duplicateMode}
            />
          </div>

          {/* Área */}
          <div className="space-y-1.5">
            <Label htmlFor="area">Área *</Label>
            <Input
              id="area"
              {...register('area')}
              placeholder="Ex: Financeiro, Comercial, TI"
              disabled={duplicateMode}
            />
            {errors.area && <p className="text-xs text-red-500">{errors.area.message}</p>}
          </div>

          {/* CPF e RG - visíveis apenas quando contractEnabled */}
          {contractEnabled && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="cpf">CPF</Label>
                <Controller
                  control={control}
                  name="cpf"
                  render={({ field: cpfField }) => (
                    <Input
                      id="cpf"
                      {...cpfField}
                      placeholder="000.000.000-00"
                      disabled={duplicateMode}
                      onChange={(e) => {
                        cpfField.onChange(maskCpf(e.target.value));
                      }}
                    />
                  )}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rg">RG</Label>
                <Controller
                  control={control}
                  name="rg"
                  render={({ field: rgField }) => (
                    <Input
                      id="rg"
                      value={rgField.value || ''}
                      onChange={(e) => rgField.onChange(maskRg(e.target.value))}
                      placeholder="RG do contato"
                      disabled={duplicateMode}
                    />
                  )}
                />
              </div>
            </div>
          )}

          {/* Tipo de Contato */}
          <div className="space-y-2">
            <Label>Tipo de Contato *</Label>
            <div className="flex flex-wrap gap-4">
              {contactForOptions.map((option) => (
                <label
                  key={option}
                  htmlFor={`contactFor-${option}`}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Checkbox
                    id={`contactFor-${option}`}
                    checked={selectedContactFor.includes(option)}
                    onCheckedChange={(checked) =>
                      handleContactForChange(option, checked as boolean)
                    }
                  />
                  <span className="text-sm">{option}</span>
                </label>
              ))}
            </div>
            {errors.contactFor && (
              <p className="text-xs text-red-500">{errors.contactFor.message}</p>
            )}
          </div>

          {/* Telefones / Emails */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Telefones / Emails *</Label>
              {!duplicateMode && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    append({
                      id: crypto.randomUUID(),
                      contact: '',
                      type: 'Celular',
                      isWhatsapp: false,
                      isPreferred: false,
                    })
                  }
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar
                </Button>
              )}
            </div>

            <div className="space-y-3">
              {fields.map((field, index) => {
                const itemType = watch(`contactItems.${index}.type`);
                const isPhone = itemType !== 'Email';
                const isCelular = itemType === 'Celular';

                return (
                  <div key={field.id} className="p-3 border rounded-lg space-y-3">
                    {/* Tipo + Opcoes + Delete */}
                    <div className="flex items-center gap-4">
                      <Controller
                        control={control}
                        name={`contactItems.${index}.type`}
                        render={({ field: typeField }) => (
                          <Select
                            value={typeField.value}
                            onValueChange={typeField.onChange}
                            disabled={duplicateMode}
                          >
                            <SelectTrigger className="w-[200px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CONTACT_TYPES.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />

                      {isCelular && (
                        <Controller
                          control={control}
                          name={`contactItems.${index}.isWhatsapp`}
                          render={({ field }) => (
                            <div className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={duplicateMode}
                              />
                              WhatsApp
                            </div>
                          )}
                        />
                      )}

                      <Controller
                        control={control}
                        name={`contactItems.${index}.isPreferred`}
                        render={({ field }) => (
                          <div className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={duplicateMode}
                            />
                            Preferido
                          </div>
                        )}
                      />

                      <div className="flex-1" />

                      {fields.length > 1 && !duplicateMode && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => remove(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    {/* Valor */}
                    <Controller
                      control={control}
                      name={`contactItems.${index}.contact`}
                      render={({ field: contactField }) => (
                        <Input
                          {...contactField}
                          placeholder={isPhone ? '(00) 00000-0000' : 'email@exemplo.com'}
                          disabled={duplicateMode}
                          onChange={(e) => {
                            contactField.onChange(
                              isPhone ? formatPhone(e.target.value) : e.target.value,
                            );
                          }}
                        />
                      )}
                    />
                    {errors.contactItems?.[index]?.contact && (
                      <p className="text-xs text-red-500">
                        {errors.contactItems[index]?.contact?.message}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {errors.contactItems && !Array.isArray(errors.contactItems) && (
              <p className="text-xs text-red-500">{errors.contactItems.message}</p>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-black text-white hover:bg-gray-800"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Salvando...'
                : duplicateMode
                  ? 'Duplicar'
                  : mode === 'edit'
                    ? 'Salvar'
                    : 'Adicionar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
